import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Image, Paperclip, Variable, Upload, Info, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { Audience, CampaignData } from '@/services/PagesServices/MarketingAndNotificationService';
import './ComposeNotification.css';

const ComposeNotification: React.FC = () => {
  const { audiences: storeAudiences, sending, createAndSendCampaign } = useMarketingAndNotificationStore();

  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync store data to local state
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences);
    }
  }, [storeAudiences]);

  // Auto-hide feedback after 5 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const resetForm = () => {
    setNotificationTitle('');
    setMessage('');
    setNotificationType('email');
    setAudiences(storeAudiences?.map(a => ({ ...a, checked: false })) || []);
    setUploadedFile(null);
    setScheduleType('now');
    setScheduleDate('');
  };

  const handleSubmit = async () => {
    // Validation
    if (!notificationTitle.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a notification title' });
      return;
    }

    if (!message.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a message' });
      return;
    }

    const selectedAudiences = audiences.filter(a => a.checked);
    if (selectedAudiences.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one audience' });
      return;
    }

    if (scheduleType === 'schedule' && !scheduleDate) {
      setFeedback({ type: 'error', message: 'Please select a schedule date' });
      return;
    }

    // Build campaign data
    const campaignData: CampaignData = {
      title: notificationTitle.trim(),
      subject: notificationTitle.trim(),
      message: message.trim(),
      targetAudience: {
        roles: selectedAudiences.map(a => a.role).filter((r): r is number => r !== undefined),
      },
      deliveryMethod: notificationType,
      scheduledFor: scheduleType === 'schedule' ? scheduleDate : null,
    };

    const sendImmediately = scheduleType === 'now';
    const result = await createAndSendCampaign(campaignData, sendImmediately);

    if (result.success) {
      setFeedback({
        type: 'success',
        message: sendImmediately
          ? 'Notification sent successfully!'
          : 'Notification scheduled successfully!'
      });
      resetForm();
    } else {
      setFeedback({
        type: 'error',
        message: result.error || 'Failed to send notification'
      });
    }
  };

  const handleAudienceToggle = (id: string) => {
    setAudiences(audiences.map(aud =>
      aud.id === id ? { ...aud, checked: !aud.checked } : aud
    ));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const selectedCount = audiences.filter(a => a.checked).length;

  return (
    <div className="admin-marketing-compose-content">
      <h2 className="admin-marketing-section-title">Compose Notification</h2>
      <p className="admin-marketing-section-subtitle">Create and send notifications to your users</p>

      <div className="admin-marketing-compose-layout">
        <div className="left-panel">
          <div className="admin-marketing-compose-card">
            <h3 className="admin-marketing-card-title">Message Details</h3>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className="admin-marketing-form-input"
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
              />
            </div>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Message</label>
              <textarea
                className="admin-marketing-form-textarea"
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="admin-marketing-toolbar">
              <button className="admin-marketing-toolbar-btn" title="Add Image">
                <Image size={16} />
                <span>Image</span>
              </button>
              <button className="admin-marketing-toolbar-btn" title="Attach File">
                <Paperclip size={16} />
                <span>Attach</span>
              </button>
              <button className="admin-marketing-toolbar-btn" title="Add Variable">
                <Variable size={16} />
                <span>Variable</span>
              </button>
            </div>
          </div>

          <div className="admin-marketing-compose-card">
            <h3 className="admin-marketing-card-title">Delivery Settings</h3>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Notification Type</label>
              <div className="admin-marketing-notification-types">
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'email' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'in-app' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`admin-marketing-type-btn ${notificationType === 'both' ? 'admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="admin-marketing-form-group">
              <label className="admin-marketing-form-label">Select Audience</label>
              <div className="admin-marketing-audience-list">
                {audiences.map(audience => (
                  <label key={audience.id} className="admin-marketing-audience-item">
                    <input
                      type="checkbox"
                      checked={audience.checked}
                      onChange={() => handleAudienceToggle(audience.id)}
                      className="admin-marketing-audience-checkbox"
                    />
                    <div className="admin-marketing-audience-info">
                      <span className="admin-marketing-audience-name">{audience.name}</span>
                      <span className="admin-marketing-audience-count">{audience.userCount} users</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-marketing-form-group">
              <div
                className="admin-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="admin-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                <p className="admin-marketing-upload-text">
                  {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
                </p>
                <p className="admin-marketing-upload-subtext">
                  or click to browse from your computer
                </p>
                <button className="admin-marketing-upload-btn" onClick={(e) => e.stopPropagation()}>
                  Select File
                </button>
                <p className="admin-marketing-upload-hint">Supports .csv files up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="admin-marketing-form-row">
              <div className="admin-marketing-form-group admin-marketing-form-group-half">
                <label className="admin-marketing-form-label">Schedule</label>
                <select
                  className="admin-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="admin-marketing-form-group admin-marketing-form-group-half">
                <label className="admin-marketing-form-label">Date & Time (Optional)</label>
                <input
                  type="date"
                  className="admin-marketing-form-input"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  disabled={scheduleType === 'now'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="admin-marketing-preview-card">
            <div className="admin-marketing-preview-header">
              <Mail size={16} className="admin-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="admin-marketing-preview-content">
              <div className="admin-marketing-preview-from">
                <span className="admin-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="admin-marketing-preview-to">
                <span className="admin-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="admin-marketing-preview-message">
                <h3 className="admin-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="admin-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="admin-marketing-ready-banner">
              <Info size={16} />
              <div className="admin-marketing-ready-content">
                <span className="admin-marketing-ready-title">Ready to Send</span>
                <ul className="admin-marketing-ready-list">
                  <li>• {selectedCount} audience{selectedCount !== 1 ? 's' : ''} selected</li>
                  <li>• Type: {notificationType}</li>
                  <li>• {scheduleType === 'now' ? 'Send immediately' : `Scheduled: ${scheduleDate}`}</li>
                </ul>
              </div>
            </div>

            {feedback && (
              <div className={`admin-marketing-feedback admin-marketing-feedback-${feedback.type}`}>
                {feedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{feedback.message}</span>
              </div>
            )}

            <button
              className="admin-marketing-send-btn"
              onClick={handleSubmit}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="admin-marketing-spinner" />
                  {scheduleType === 'now' ? 'Sending...' : 'Scheduling...'}
                </>
              ) : (
                <>
                  <Mail size={18} />
                  {scheduleType === 'now' ? 'Send Notification' : 'Schedule Notification'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeNotification;
